import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { jobsApi, type Job } from '@/api/services/jobs.api';
import { getErrorMessage } from '@/api/client';
import {
  Plus,
  RefreshCw,
  MoreHorizontal,
  Briefcase,
  Undo2,
  Edit,
  Trash2,
  ClipboardList,
  Ruler,
  Package,
  Hash,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { yardApi, YardPipeTally as YardPipeTallyType } from '@/api/services/yard.api';
import {
  PIPE_GRADES,
  PIPE_CONDITIONS,
  PIPE_TYPES,
  getGradeLabel,
  pipeConditionBadge,
} from '@/lib/pipe';
import { locationsApi } from '@/api/services/locations.api';
import { PageHeader, MetricCard, DataTable, TableFilters, KPIGrid } from '@/components/common';
import type { Column } from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

type PipeGrade =
  | 'API_5CT_J55'
  | 'API_5CT_K55'
  | 'API_5CT_L80'
  | 'API_5CT_N80'
  | 'API_5CT_C90'
  | 'API_5CT_T95'
  | 'API_5CT_P110'
  | 'API_5CT_Q125'
  | 'OTHER';
type PipeCondition = 'NEW' | 'PREMIUM' | 'CLASS_2' | 'CLASS_3' | 'SCRAP';

export default function YardPipeTallyPage() {
  const navigate = useNavigate();
  // Arrived from a job's readiness panel asking what pipe is allocated to it.
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId') ?? undefined;
  const [allocating, setAllocating] = useState<YardPipeTallyType | null>(null);
  const [openJobs, setOpenJobs] = useState<Job[]>([]);

  const [tallies, setTallies] = useState<YardPipeTallyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTallies: 0,
    totalJoints: 0,
    totalFootage: 0,
    inStock: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [pipeTypeFilter, setPipeTypeFilter] = useState<string>('');
  const [gradeFilter, setGradeFilter] = useState<string>('');

  // Locations for form
  const [locations, setLocations] = useState<any[]>([]);

  // Create/Edit dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTally, setEditingTally] = useState<YardPipeTallyType | null>(null);
  const [formData, setFormData] = useState({
    locationId: '',
    pipeType: '',
    outerDiameter: '',
    wallThickness: '',
    weight: '',
    grade: '' as PipeGrade | '',
    connection: '',
    range: '',
    condition: 'NEW' as PipeCondition,
    heatNumber: '',
    millName: '',
    storageLocation: '',
    notes: '',
  });

  // Tally details sheet

  // Add joints dialog
  const [isAddJointsDialogOpen, setIsAddJointsDialogOpen] = useState(false);
  const [tallyForJoints, setTallyForJoints] = useState<YardPipeTallyType | null>(null);
  const [jointsFormData, setJointsFormData] = useState({
    joints: [{ length: '', serialNumber: '', condition: 'NEW' as PipeCondition }],
  });

  // Perform tally dialog
  const [isTallyDialogOpen, setIsTallyDialogOpen] = useState(false);
  const [tallyToCount, setTallyToCount] = useState<YardPipeTallyType | null>(null);
  const [tallyCountData, setTallyCountData] = useState({
    joints: '',
    totalFootage: '',
    notes: '',
  });

  const fetchTallies = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (pipeTypeFilter) params.pipeType = pipeTypeFilter;
      if (gradeFilter) params.grade = gradeFilter;
      if (jobId) params.jobId = jobId;

      const response = await yardApi.pipeTally.getAll(params);
      setTallies(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      setTallies([]);
      console.error('Error fetching pipe tallies:', error);
      toast.error('Failed to load pipe tallies');
    } finally {
      setLoading(false);
    }
  }, [pipeTypeFilter, gradeFilter, jobId]);
  const fetchStats = useCallback(async () => {
    try {
      // getStats, not getSummary: the summary nests its totals under `totals`
      // with different names, so reading totalTallies off it always yielded the
      // zero default while the table below listed real rows.
      const response = await yardApi.pipeTally.getStats();
      const defaults = { totalTallies: 0, totalJoints: 0, totalFootage: 0, inStock: 0 };
      const statsData = typeof response === 'object' && response !== null ? response : {};
      setStats({ ...defaults, ...statsData });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);
  const fetchLocations = useCallback(async () => {
    try {
      const response = await locationsApi.getAll();
      setLocations(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }, []);

  useEffect(() => {
    fetchTallies();
    fetchStats();
    fetchLocations();
  }, [fetchTallies, fetchStats, fetchLocations]);

  // Jobs a tally can be committed to. Only open ones: allocating pipe to a
  // finished job would be recording a decision nobody can act on.
  useEffect(() => {
    jobsApi
      .getAll()
      .then((res) =>
        setOpenJobs((res.data ?? []).filter((j) => j.status === 'PLANNED' || j.status === 'ACTIVE'))
      )
      .catch(() => setOpenJobs([]));
  }, []);

  const handleCreateTally = async () => {
    if (!formData.locationId || !formData.pipeType || !formData.grade) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        locationId: formData.locationId,
        pipeType: formData.pipeType,
        outerDiameter: parseFloat(formData.outerDiameter),
        wallThickness: parseFloat(formData.wallThickness),
        weight: parseFloat(formData.weight),
        grade: formData.grade as PipeGrade,
        connection: formData.connection || undefined,
        range: formData.range || undefined,
        condition: formData.condition,
        heatNumber: formData.heatNumber || undefined,
        millName: formData.millName || undefined,
        storageLocation: formData.storageLocation || undefined,
        notes: formData.notes || undefined,
      };

      if (editingTally) {
        await yardApi.pipeTally.update(editingTally.id, payload);
        toast.success('Pipe tally updated');
      } else {
        await yardApi.pipeTally.create(payload);
        toast.success('Pipe tally created');
      }

      setIsCreateDialogOpen(false);
      setEditingTally(null);
      resetForm();
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error saving pipe tally:', error);
      toast.error('Failed to save pipe tally');
    }
  };

  const handleDeleteTally = async (tally: YardPipeTallyType) => {
    if (!confirm('Are you sure you want to delete this pipe tally?')) return;

    try {
      await yardApi.pipeTally.delete(tally.id);
      toast.success('Pipe tally deleted');
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error deleting pipe tally:', error);
      toast.error('Failed to delete pipe tally');
    }
  };

  const handleAddJoints = async () => {
    if (!tallyForJoints) return;

    const validJoints = jointsFormData.joints.filter((j) => j.length);
    if (validJoints.length === 0) {
      toast.error('Please add at least one joint');
      return;
    }

    try {
      await yardApi.pipeTally.addJoints(
        tallyForJoints.id,
        validJoints.map((j) => ({
          length: parseFloat(j.length),
          serialNumber: j.serialNumber || undefined,
          condition: j.condition,
        }))
      );
      toast.success('Joints added successfully');
      setIsAddJointsDialogOpen(false);
      setTallyForJoints(null);
      setJointsFormData({ joints: [{ length: '', serialNumber: '', condition: 'NEW' }] });
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error adding joints:', error);
      toast.error('Failed to add joints');
    }
  };

  const handlePerformTally = async () => {
    if (!tallyToCount) return;

    try {
      await yardApi.pipeTally.performTally(tallyToCount.id, {
        joints: parseInt(tallyCountData.joints),
        totalFootage: parseFloat(tallyCountData.totalFootage),
        notes: tallyCountData.notes || undefined,
      });
      toast.success('Tally count updated');
      setIsTallyDialogOpen(false);
      setTallyToCount(null);
      setTallyCountData({ joints: '', totalFootage: '', notes: '' });
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error performing tally:', error);
      toast.error('Failed to update tally');
    }
  };

  const openEditDialog = (tally: YardPipeTallyType) => {
    setEditingTally(tally);
    setFormData({
      locationId: tally.locationId,
      pipeType: tally.pipeType,
      outerDiameter: tally.outerDiameter.toString(),
      wallThickness: tally.wallThickness.toString(),
      weight: tally.weight.toString(),
      grade: tally.grade as PipeGrade,
      connection: tally.connection || '',
      range: tally.range || '',
      condition: tally.condition as PipeCondition,
      heatNumber: tally.heatNumber || '',
      millName: tally.millName || '',
      storageLocation: tally.storageLocation || '',
      notes: tally.notes || '',
    });
    setIsCreateDialogOpen(true);
  };

  const openAddJointsDialog = (tally: YardPipeTallyType) => {
    setTallyForJoints(tally);
    setJointsFormData({ joints: [{ length: '', serialNumber: '', condition: 'NEW' }] });
    setIsAddJointsDialogOpen(true);
  };

  const openTallyDialog = (tally: YardPipeTallyType) => {
    setTallyToCount(tally);
    setTallyCountData({
      joints: tally.joints.toString(),
      totalFootage: tally.totalFootage.toString(),
      notes: '',
    });
    setIsTallyDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      locationId: '',
      pipeType: '',
      outerDiameter: '',
      wallThickness: '',
      weight: '',
      grade: '',
      connection: '',
      range: '',
      condition: 'NEW',
      heatNumber: '',
      millName: '',
      storageLocation: '',
      notes: '',
    });
  };

  const addJointRow = () => {
    setJointsFormData((prev) => ({
      joints: [...prev.joints, { length: '', serialNumber: '', condition: 'NEW' as PipeCondition }],
    }));
  };

  const removeJointRow = (index: number) => {
    setJointsFormData((prev) => ({
      joints: prev.joints.filter((_, i) => i !== index),
    }));
  };

  const updateJointRow = (index: number, field: string, value: string) => {
    setJointsFormData((prev) => ({
      joints: prev.joints.map((j, i) => (i === index ? { ...j, [field]: value } : j)),
    }));
  };

  const getConditionBadge = (condition: PipeCondition) => {
    const { variant, label } = pipeConditionBadge(condition);
    return <Badge variant={variant}>{label}</Badge>;
  };

  const filteredTallies = tallies.filter((tally) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesNumber = tally.tallyNumber?.toLowerCase().includes(query);
      const matchesHeat = tally.heatNumber?.toLowerCase().includes(query);
      const matchesMill = tally.millName?.toLowerCase().includes(query);
      if (!matchesNumber && !matchesHeat && !matchesMill) return false;
    }
    return true;
  });

  /**
   * Commit pipe to a job, or release it back to the yard.
   *
   * This is the only way `jobId` is ever set, and without it the readiness
   * material gate could never see a job's casing — the field would exist and
   * stay null forever.
   */
  const allocate = async (tally: YardPipeTallyType, nextJobId: string | null) => {
    try {
      await yardApi.pipeTally.update(tally.id, { jobId: nextJobId });
      toast.success(
        nextJobId
          ? `${tally.tallyNumber} allocated`
          : `${tally.tallyNumber} released back to the yard`
      );
      setAllocating(null);
      await fetchTallies();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const pipeTallyColumns: Column<YardPipeTallyType>[] = [
    {
      key: 'tallyNumber',
      header: 'Tally #',
      sortable: true,
      linkTo: (tally) => `/yard/pipe-tally/${tally.id}`,
    },
    {
      key: 'job',
      header: 'Allocated to',
      render: (tally) =>
        tally.job ? (
          <Link
            to={`/jobs/${tally.job.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary underline-offset-4 hover:underline"
          >
            {tally.job.jobNumber}
          </Link>
        ) : (
          // Unallocated is the normal state — casing is bought against a
          // programme long before it is committed to a hole.
          <span className="text-xs text-muted-foreground">In yard</span>
        ),
    },
    {
      key: 'pipeType',
      header: 'Type / Grade',
      sortable: true,
      render: (tally) => (
        <div>
          <p>{PIPE_TYPES.find((t) => t.value === tally.pipeType)?.label || tally.pipeType}</p>
          <Badge variant="outline" className="mt-1">
            {getGradeLabel(tally.grade as PipeGrade)}
          </Badge>
        </div>
      ),
    },
    {
      key: 'outerDiameter',
      header: 'OD x Wall',
      render: (tally) => (
        <div>
          {tally.outerDiameter}" x {tally.wallThickness}"
          <p className="text-sm text-muted-foreground">{tally.weight} lb/ft</p>
        </div>
      ),
    },
    {
      key: 'joints',
      header: 'Joints',
      sortable: true,
      render: (tally) => <span>{tally.joints.toLocaleString()}</span>,
    },
    {
      key: 'totalFootage',
      header: 'Footage',
      sortable: true,
      render: (tally) => <span>{tally.totalFootage.toLocaleString()} ft</span>,
    },
    {
      key: 'condition',
      header: 'Condition',
      render: (tally) => getConditionBadge(tally.condition as PipeCondition),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (tally) => (
        <Badge variant={tally.status === 'IN_STOCK' ? 'default' : 'secondary'}>
          {tally.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (tally) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEditDialog(tally)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAllocating(tally)}>
              <Briefcase className="h-4 w-4 mr-2" />
              {tally.job ? 'Reallocate' : 'Allocate to job'}
            </DropdownMenuItem>
            {tally.job && (
              <DropdownMenuItem onClick={() => allocate(tally, null)}>
                <Undo2 className="h-4 w-4 mr-2" />
                Release to yard
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => openAddJointsDialog(tally)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Joints
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openTallyDialog(tally)}>
              <ClipboardList className="h-4 w-4 mr-2" />
              Perform Tally
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteTally(tally)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Pipe Tally"
          description="Tubular stock counted by joint, with grade, dimensions, footage, and condition."
          actions={
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  fetchTallies();
                  fetchStats();
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setEditingTally(null);
                  setIsCreateDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Pipe Tally
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Tallies"
            value={stats.totalTallies}
            icon={<FileText className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Total Joints"
            value={(stats.totalJoints ?? 0).toLocaleString()}
            icon={<Hash className="h-5 w-5" />}
            sentiment="neutral"
          />
          <MetricCard
            label="Total Footage"
            value={`${(stats.totalFootage ?? 0).toLocaleString()} ft`}
            icon={<Ruler className="h-5 w-5" />}
            sentiment="neutral"
          />
          <MetricCard
            label="In Stock"
            value={stats.inStock}
            icon={<Package className="h-5 w-5" />}
            sentiment="success"
          />
        </KPIGrid>
      </motion.div>

      {/* Filters */}
      <motion.div variants={fadeUp}>
        {/* Tallies Table */}
        <DataTable<YardPipeTallyType>
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search by tally number, heat number, or mill..."
              filters={[
                {
                  id: 'pipeType',
                  label: 'Types',
                  value: pipeTypeFilter,
                  options: PIPE_TYPES,
                  onChange: setPipeTypeFilter,
                  placeholder: 'Pipe Type',
                },
                {
                  id: 'grade',
                  label: 'Grades',
                  value: gradeFilter,
                  options: PIPE_GRADES,
                  onChange: setGradeFilter,
                  placeholder: 'Grade',
                },
              ]}
            />
          }
          onRowClick={(tally) => navigate(`/yard/pipe-tally/${tally.id}`)}
          pagination
          data={filteredTallies}
          columns={pipeTallyColumns}
          loading={loading}
          emptyTitle="No pipe tallies found"
          emptyDescription="Create a new pipe tally to get started."
        />

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTally ? 'Edit Pipe Tally' : 'New Pipe Tally'}</DialogTitle>
              <DialogDescription>
                {editingTally
                  ? 'Update pipe tally information.'
                  : 'Create a new pipe tally record.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="locationId">Location *</Label>
                  <Select
                    value={formData.locationId}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, locationId: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pipeType">Pipe Type *</Label>
                  <Select
                    value={formData.pipeType}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, pipeType: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="outerDiameter">OD (inches) *</Label>
                  <Input
                    id="outerDiameter"
                    type="number"
                    step="0.001"
                    value={formData.outerDiameter}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, outerDiameter: e.target.value }))
                    }
                    placeholder="e.g., 7.0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallThickness">Wall (inches) *</Label>
                  <Input
                    id="wallThickness"
                    type="number"
                    step="0.001"
                    value={formData.wallThickness}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, wallThickness: e.target.value }))
                    }
                    placeholder="e.g., 0.408"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (lb/ft) *</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.01"
                    value={formData.weight}
                    onChange={(e) => setFormData((prev) => ({ ...prev, weight: e.target.value }))}
                    placeholder="e.g., 29.0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="grade">Grade *</Label>
                  <Select
                    value={formData.grade}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, grade: value as PipeGrade }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPE_GRADES.map((grade) => (
                        <SelectItem key={grade.value} value={grade.value}>
                          {grade.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="condition">Condition</Label>
                  <Select
                    value={formData.condition}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, condition: value as PipeCondition }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPE_CONDITIONS.map((cond) => (
                        <SelectItem key={cond.value} value={cond.value}>
                          {cond.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="connection">Connection Type</Label>
                  <Input
                    id="connection"
                    value={formData.connection}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, connection: e.target.value }))
                    }
                    placeholder="e.g., BTC, LTC, STC"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="range">Range</Label>
                  <Select
                    value={formData.range}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, range: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="R1">R1 (18-22 ft)</SelectItem>
                      <SelectItem value="R2">R2 (28-32 ft)</SelectItem>
                      <SelectItem value="R3">R3 (38-45 ft)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="heatNumber">Heat Number</Label>
                  <Input
                    id="heatNumber"
                    value={formData.heatNumber}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, heatNumber: e.target.value }))
                    }
                    placeholder="Mill heat number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="millName">Mill Name</Label>
                  <Input
                    id="millName"
                    value={formData.millName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, millName: e.target.value }))}
                    placeholder="e.g., Tenaris, Vallourec"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="storageLocation">Storage Location</Label>
                <Input
                  id="storageLocation"
                  value={formData.storageLocation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, storageLocation: e.target.value }))
                  }
                  placeholder="Yard location reference"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTally}>
                {editingTally ? 'Update' : 'Create'} Tally
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Joints Dialog */}
        <Dialog open={isAddJointsDialogOpen} onOpenChange={setIsAddJointsDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Add Joints</DialogTitle>
              <DialogDescription>Add individual pipe joints to this tally.</DialogDescription>
            </DialogHeader>
            <div className="py-4 max-h-[50vh] overflow-y-auto space-y-3">
              {jointsFormData.joints.map((joint, index) => (
                <div key={index} className="flex items-end gap-2 p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Length (ft)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={joint.length}
                      onChange={(e) => updateJointRow(index, 'length', e.target.value)}
                      placeholder="Length"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Serial #</Label>
                    <Input
                      value={joint.serialNumber}
                      onChange={(e) => updateJointRow(index, 'serialNumber', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label className="text-xs">Condition</Label>
                    <Select
                      value={joint.condition}
                      onValueChange={(value) => updateJointRow(index, 'condition', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PIPE_CONDITIONS.map((cond) => (
                          <SelectItem key={cond.value} value={cond.value}>
                            {cond.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {jointsFormData.joints.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeJointRow(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" className="w-full" onClick={addJointRow}>
                <Plus className="h-4 w-4 mr-2" />
                Add Another Joint
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddJointsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddJoints}>Add Joints</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Perform Tally Dialog */}
        <Dialog open={isTallyDialogOpen} onOpenChange={setIsTallyDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Perform Tally Count</DialogTitle>
              <DialogDescription>
                Update the joint count and footage for this pipe tally.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="joints">Joint Count</Label>
                <Input
                  id="joints"
                  type="number"
                  value={tallyCountData.joints}
                  onChange={(e) =>
                    setTallyCountData((prev) => ({ ...prev, joints: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totalFootage">Total Footage (ft)</Label>
                <Input
                  id="totalFootage"
                  type="number"
                  step="0.01"
                  value={tallyCountData.totalFootage}
                  onChange={(e) =>
                    setTallyCountData((prev) => ({ ...prev, totalFootage: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tallyNotes">Notes</Label>
                <Textarea
                  id="tallyNotes"
                  value={tallyCountData.notes}
                  onChange={(e) =>
                    setTallyCountData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Tally notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsTallyDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handlePerformTally}>
                <ClipboardList className="h-4 w-4 mr-2" />
                Update Tally
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Allocating pipe to a job. A dialog because it is an action taken on a
          record, not a record to read — the same rule the rest of the product
          follows. */}
      <Dialog open={!!allocating} onOpenChange={(open) => !open && setAllocating(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allocate {allocating?.tallyNumber}</DialogTitle>
            <DialogDescription>
              {allocating?.joints} joint(s) at {allocating?.location.name}. Allocating tells job
              readiness this pipe is committed; it does not move it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="allocate-job">Job</Label>
            <Select onValueChange={(v) => allocating && allocate(allocating, v)}>
              <SelectTrigger id="allocate-job">
                <SelectValue placeholder="Choose a job" />
              </SelectTrigger>
              <SelectContent>
                {openJobs.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    No planned or active job to allocate to.
                  </div>
                ) : (
                  openJobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.jobNumber} — {j.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocating(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
